import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import {
  Upload, Palette, Type, Save, RefreshCw, Store,
  Check, AlertCircle, X, Image as ImageIcon, Glasses,
  FileText, Receipt, GripVertical, ChevronDown,
  Eye, AlignLeft, AlignCenter, AlignRight, Hash, Tag,
} from 'lucide-react';
import axios from 'axios';
import InvoiceLegalPanel from './InvoiceLegalPanel';
import { DEFAULT_BRANDING, useBranding } from '../../contexts/BrandingContext';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_INVOICE_TEMPLATE as DEFAULT_INVOICE_TEMPLATE_BASE,
  DEFAULT_RECEIPT_TEMPLATE as DEFAULT_RECEIPT_TEMPLATE_BASE,
  DEFAULT_INVOICE_LABELS,
  DEFAULT_RECEIPT_LABELS,
  DEFAULT_ORDER_NUMBER_FORMAT,
  previewOrderNumber,
} from '../../utils/receiptTemplate';

// ─── CSS-var token shortcuts ──────────────────────────────────────────────────
const T = {
  brand:    'var(--c-brand)',
  ink:      'var(--c-ink)',
  body:     'var(--c-body)',
  muted:    'var(--c-muted)',
  bg:       'var(--c-bg)',
  surface:  'var(--c-surface)',
  soft:     'var(--c-surface-soft)',
  hairline: 'var(--c-hairline)',
  hover:    'var(--c-hover)',
  shadow:   'var(--shadow)',
  shadowMd: 'var(--shadow-md)',
};

// Re-export shared template defaults so consumers (sidebar, tests) keep
// working off these names. The actual values live in utils/receiptTemplate.js.
export const DEFAULT_INVOICE_TEMPLATE = DEFAULT_INVOICE_TEMPLATE_BASE;
export const DEFAULT_RECEIPT_TEMPLATE = DEFAULT_RECEIPT_TEMPLATE_BASE;

// ─── Sample data for live previews ───────────────────────────────────────────
const SAMPLE = {
  orderNumber: 'INV-2024-001',
  date: new Date().toLocaleDateString(),
  dueDate: new Date(Date.now() + 7 * 86400000).toLocaleDateString(),
  salesPerson: 'EMP-0042',
  technician: 'Khaled Idir',
  clientName: 'Fatima Zohra',
  clientPhone: '+213 555 123 456',
  clientGender: 'Female',
  clientAge: '25–34',
  visitNumber: 3,
  items: [
    { name: 'Progressive Anti-Reflex', lensType: 'Progressive', sph: '–1.50', cyl: '–0.75', qty: 1, unitPrice: 6500, total: 6500 },
    { name: 'Titanium Frame "Axiom"', lensType: '—', sph: '—', cyl: '—', qty: 1, unitPrice: 9500, total: 9500 },
  ],
  subtotal: 16000,
  discount: 1000,
  total: 15000,
  amountPaid: 8000,
  remaining: 7000,
};

// ─── Section metadata ─────────────────────────────────────────────────────────
const SECTIONS = {
  header:  { label: 'Header Block',      icon: AlignLeft },
  meta:    { label: 'Invoice Meta',      icon: FileText },
  client:  { label: 'Client Info',       icon: Glasses },
  items:   { label: 'Items Table',       icon: Receipt },
  totals:  { label: 'Totals Block',      icon: ChevronDown },
  footer:  { label: 'Footer',            icon: AlignCenter },
};

// ─── Layout ───────────────────────────────────────────────────────────────────
const Shell = styled.div`
  display: grid;
  grid-template-columns: 240px 1fr;
  height: 100%;
  min-height: 0;
  overflow: hidden;
`;

const StoreListPanel = styled.aside`
  background: ${T.surface};
  border-right: 1px solid ${T.hairline};
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const PanelHead = styled.div`
  padding: 1rem 0.875rem 0.75rem;
  border-bottom: 1px solid ${T.hairline};
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-size: 0.75rem;
  font-weight: 700;
  color: ${T.muted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const StoreScroll = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 0.375rem;
`;

const StoreItem = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.625rem;
  border: none;
  border-radius: 8px;
  background: ${p => p.active ? T.brand : 'transparent'};
  color: ${p => p.active ? 'white' : T.ink};
  font-size: 0.8125rem;
  font-weight: ${p => p.active ? '600' : '400'};
  font-family: inherit;
  cursor: pointer;
  text-align: left;
  transition: background 0.15s;
  margin-bottom: 2px;
  &:hover { background: ${p => p.active ? T.brand : T.hover}; }
  .dot {
    width: 7px; height: 7px; border-radius: 50%; flex-shrink: 0; margin-left: auto;
    background: ${p => p.hasBranding ? '#10b981' : T.hairline};
  }
`;

// ─── Right (editor) panel ─────────────────────────────────────────────────────
const EditorPanel = styled.main`
  background: ${T.bg};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const EmptyHint = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  color: ${T.muted};
  text-align: center;
  padding: 3rem;
  svg { opacity: 0.2; }
  p { margin: 0; font-size: 0.875rem; }
  strong { color: ${T.body}; font-size: 1rem; }
`;

// ─── Tabs ─────────────────────────────────────────────────────────────────────
const TabBar = styled.div`
  display: flex;
  border-bottom: 1px solid ${T.hairline};
  background: ${T.surface};
  padding: 0 1.25rem;
  gap: 0;
  flex-shrink: 0;
`;

const Tab = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.375rem;
  padding: 0.75rem 1rem;
  border: none;
  border-bottom: 2px solid ${p => p.active ? T.brand : 'transparent'};
  margin-bottom: -1px;
  background: transparent;
  color: ${p => p.active ? T.brand : T.muted};
  font-size: 0.8125rem;
  font-weight: ${p => p.active ? '600' : '500'};
  font-family: inherit;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s;
  white-space: nowrap;
  &:hover { color: ${T.brand}; }
`;

const TabContent = styled.div`
  padding: 1.25rem 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
  flex: 1;
`;

// ─── Shared form primitives ───────────────────────────────────────────────────
const SectionCard = styled.div`
  background: ${T.surface};
  border: 1px solid ${T.hairline};
  border-radius: 12px;
  box-shadow: ${T.shadow};
  overflow: hidden;
`;

const CardHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-bottom: 1px solid ${T.hairline};
  font-size: 0.875rem;
  font-weight: 700;
  color: ${T.ink};
  svg { color: ${T.brand}; flex-shrink: 0; }
`;

const CardBody = styled.div`
  padding: 1rem;
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0.875rem;
  @media (max-width: 700px) { grid-template-columns: 1fr; }
`;

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const FieldLabel = styled.label`
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${T.body};
`;

const TextInput = styled.input`
  padding: 0.5rem 0.6875rem;
  border: 1.5px solid ${T.hairline};
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${T.ink};
  background: ${T.surface};
  outline: none;
  transition: border-color 0.15s;
  &:focus { border-color: ${T.brand}; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
`;

const TextArea = styled.textarea`
  padding: 0.5rem 0.6875rem;
  border: 1.5px solid ${T.hairline};
  border-radius: 8px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${T.ink};
  background: ${T.surface};
  outline: none;
  resize: vertical;
  min-height: 64px;
  line-height: 1.5;
  transition: border-color 0.15s;
  &:focus { border-color: ${T.brand}; box-shadow: 0 0 0 3px rgba(99,102,241,0.1); }
`;

// ─── Toggle switch ────────────────────────────────────────────────────────────
const ToggleWrap = styled.label`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.375rem 0;
  cursor: pointer;
  user-select: none;
  gap: 0.5rem;
`;

const ToggleLabel = styled.span`
  font-size: 0.8125rem;
  color: ${T.body};
  flex: 1;
`;

const ToggleTrack = styled.div`
  width: 34px; height: 18px;
  border-radius: 9px;
  background: ${p => p.on ? T.brand : T.hairline};
  position: relative;
  flex-shrink: 0;
  transition: background 0.2s;
  &::after {
    content: '';
    position: absolute;
    top: 2px;
    left: ${p => p.on ? '18px' : '2px'};
    width: 14px; height: 14px;
    background: white;
    border-radius: 50%;
    transition: left 0.2s;
    box-shadow: 0 1px 3px rgba(0,0,0,0.2);
  }
`;

const Toggle = ({ checked, onChange, label }) => (
  <ToggleWrap>
    <ToggleLabel>{label}</ToggleLabel>
    <ToggleTrack on={checked} onClick={onChange} />
  </ToggleWrap>
);

// ─── Radio group ──────────────────────────────────────────────────────────────
const RadioRow = styled.div`
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
`;

const RadioChip = styled.label`
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.3125rem 0.75rem;
  border-radius: 20px;
  border: 1.5px solid ${p => p.active ? T.brand : T.hairline};
  background: ${p => p.active ? 'var(--c-brand-soft)' : T.surface};
  color: ${p => p.active ? T.brand : T.body};
  font-size: 0.8rem;
  font-weight: ${p => p.active ? '600' : '500'};
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${T.brand}; }
  input { display: none; }
`;

const SizeChip = RadioChip;

// ─── Action row ───────────────────────────────────────────────────────────────
const ActionRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  flex-wrap: wrap;
  padding: 0.5rem 0;
`;

const SaveBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5625rem 1.25rem;
  background: ${T.brand};
  color: white;
  border: none;
  border-radius: 9px;
  font-weight: 600;
  font-size: 0.875rem;
  font-family: inherit;
  cursor: pointer;
  transition: opacity 0.15s, transform 0.15s;
  &:hover:not(:disabled) { opacity: 0.88; transform: translateY(-1px); }
  &:disabled { opacity: 0.5; cursor: not-allowed; transform: none; }
`;

const GhostBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5625rem 1rem;
  background: none;
  border: 1.5px solid ${T.hairline};
  border-radius: 9px;
  font-size: 0.875rem;
  font-family: inherit;
  color: ${T.body};
  cursor: pointer;
  transition: border-color 0.15s;
  &:hover { border-color: ${T.brand}; }
`;

const FeedbackBanner = styled.div`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 0.875rem;
  border-radius: 9px;
  font-size: 0.875rem;
  font-weight: 500;
  background: ${p => p.ok ? '#f0fdf4' : '#fef2f2'};
  color: ${p => p.ok ? '#16a34a' : '#dc2626'};
  border: 1px solid ${p => p.ok ? '#bbf7d0' : '#fecaca'};
`;

// ─── Branding tab sub-components ──────────────────────────────────────────────
const LogoZone = styled.div`
  border: 2px dashed ${p => p.drag ? T.brand : T.hairline};
  border-radius: 10px;
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.625rem;
  cursor: pointer;
  transition: border-color 0.15s, background 0.15s;
  background: ${p => p.drag ? 'var(--c-brand-soft)' : T.soft};
  min-height: 120px;
  &:hover { border-color: ${T.brand}; }
`;

const LogoPreviewImg = styled.img`
  max-height: 64px;
  max-width: 180px;
  object-fit: contain;
  border-radius: 6px;
`;

const ColorPickerRow = styled.div`
  display: flex;
  gap: 1rem;
  flex-wrap: wrap;
`;

const ColorBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const ColorSwatch = styled.label`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.4375rem 0.625rem;
  border: 1.5px solid ${T.hairline};
  border-radius: 8px;
  cursor: pointer;
  background: ${T.surface};
  &:hover { border-color: ${T.brand}; }
`;

const ColorCircle = styled.div`
  width: 24px; height: 24px;
  border-radius: 50%;
  background: ${p => p.color};
  border: 2px solid rgba(0,0,0,0.08);
  flex-shrink: 0;
`;

const HiddenColorInput = styled.input`
  opacity: 0; position: absolute; width: 0; height: 0; pointer-events: none;
`;

const ColorHex = styled.span`
  font-size: 0.8rem; font-weight: 600; font-family: monospace; color: ${T.body};
`;

const PreviewShell = styled.div`
  border: 1px solid ${T.hairline};
  border-radius: 10px;
  overflow: hidden;
  background: white;
  box-shadow: ${T.shadowMd};
  max-width: 380px;
`;

const PreviewHeader = styled.div`
  background: ${p => p.primary};
  padding: 0.875rem 1rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
`;

// ─── Invoice Builder ──────────────────────────────────────────────────────────

// Draggable section row
const DragRow = styled.div`
  border: 1.5px solid ${p => p.dragOver ? T.brand : T.hairline};
  border-radius: 10px;
  margin-bottom: 0.5rem;
  background: ${T.surface};
  overflow: hidden;
  opacity: ${p => p.dragging ? 0.45 : 1};
  transition: border-color 0.15s, opacity 0.15s;
`;

const DragRowHead = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  user-select: none;
  &:hover { background: ${T.hover}; }
`;

const DragHandle = styled.div`
  cursor: grab;
  color: ${T.muted};
  display: flex;
  align-items: center;
  flex-shrink: 0;
  padding: 2px;
  &:active { cursor: grabbing; }
`;

const DragRowLabel = styled.span`
  font-size: 0.875rem;
  font-weight: 600;
  color: ${T.ink};
  flex: 1;
`;

const ExpandIcon = styled.span`
  color: ${T.muted};
  display: flex;
  align-items: center;
  transition: transform 0.2s;
  transform: ${p => p.open ? 'rotate(180deg)' : 'none'};
`;

const DragRowBody = styled.div`
  padding: 0.75rem 1rem;
  border-top: 1px solid ${T.hairline};
  display: ${p => p.open ? 'block' : 'none'};
  background: ${T.soft};
`;

const SubLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${T.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin: 0.625rem 0 0.25rem;
  &:first-child { margin-top: 0; }
`;

// ─── Label-editor row (shared by invoice + receipt builders) ─────────────────
const LabelRow = styled.div`
  display: grid;
  grid-template-columns: 110px 1fr;
  gap: 0.5rem;
  align-items: center;
  margin-bottom: 0.375rem;
  & > label {
    font-size: 0.75rem;
    color: ${T.muted};
    text-transform: capitalize;
  }
`;

const LabelInput = styled.input`
  padding: 0.3125rem 0.5rem;
  border: 1px solid ${T.hairline};
  border-radius: 6px;
  font-size: 0.78rem;
  font-family: inherit;
  color: ${T.ink};
  background: ${T.surface};
  outline: none;
  &:focus { border-color: ${T.brand}; box-shadow: 0 0 0 2px rgba(99,102,241,0.1); }
`;

// Renders a list of {key, label} into an editable label grid
const LabelEditor = ({ entries, labels, defaults, onChange }) => (
  <div>
    {entries.map(([key, friendlyName]) => (
      <LabelRow key={key}>
        <label htmlFor={`lbl-${key}`}>{friendlyName}</label>
        <LabelInput
          id={`lbl-${key}`}
          value={labels?.[key] ?? ''}
          placeholder={defaults[key] || ''}
          onChange={(e) => onChange({ ...labels, [key]: e.target.value })}
        />
      </LabelRow>
    ))}
  </div>
);

// ─── Invoice Live Preview ─────────────────────────────────────────────────────
const InvPaper = styled.div`
  background: white;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 11px;
  color: #111;
  width: 296px;
  min-height: 420px;
  padding: 18px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
  margin: 0 auto;
  position: relative;
  &::before {
    content: 'A5 portrait';
    position: absolute;
    top: -22px;
    right: 0;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

const InvHeaderWrap = styled.div`
  display: flex;
  justify-content: ${p =>
    p.pos === 'center' ? 'center' :
    p.pos === 'right'  ? 'flex-end' : 'flex-start'};
  align-items: center;
  gap: 10px;
  padding-bottom: 12px;
  border-bottom: 2px solid ${p => p.primary};
  margin-bottom: 12px;
  text-align: ${p => p.pos === 'center' ? 'center' : 'left'};
`;

const InvLogoBox = styled.div`
  width: 36px; height: 36px;
  border-radius: 6px;
  background: ${p => p.primary};
  display: flex; align-items: center; justify-content: center;
  overflow: hidden; flex-shrink: 0;
  img { width: 100%; height: 100%; object-fit: contain; }
`;

const InvStoreName = styled.div`
  font-size: 13px; font-weight: 700; color: ${p => p.primary};
`;

const InvTagline = styled.div`
  font-size: 9px; color: #888;
`;

const InvMetaGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
  margin-bottom: 10px;
`;

const InvMetaItem = styled.div`
  .lbl { font-size: 8px; text-transform: uppercase; letter-spacing: 0.06em; color: #94a3b8; }
  .val { font-size: 10px; font-weight: 600; color: #111; }
`;

const InvClientGrid = styled.div`
  background: #f8fafc;
  border-radius: 6px;
  padding: 8px 10px;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4px 16px;
  margin-bottom: 10px;
`;

const InvTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 10px;
  font-size: 10px;
  th {
    background: ${p => p.primary}11;
    color: ${p => p.primary};
    padding: 5px 6px;
    text-align: left;
    font-size: 8px;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    border-bottom: 1.5px solid ${p => p.primary}33;
  }
  td {
    padding: 5px 6px;
    border-bottom: 1px solid #f1f5f9;
    color: #334155;
  }
`;

const InvTotals = styled.div`
  margin-left: auto;
  width: 200px;
  font-size: 10px;
  border-top: 1.5px solid #e2e8f0;
  padding-top: 6px;
  .row { display: flex; justify-content: space-between; padding: 2px 0; }
  .total { font-weight: 700; color: ${p => p.primary}; font-size: 11px; border-top: 1px solid #e2e8f0; margin-top: 4px; padding-top: 4px; }
`;

const InvFooter = styled.div`
  margin-top: 14px;
  border-top: 1px solid #f1f5f9;
  padding-top: 10px;
  font-size: ${p => p.size === 'small' ? '8px' : p.size === 'large' ? '11px' : '9px'};
  color: #64748b;
  text-align: center;
`;

// ─── Receipt (A5 portrait) preview ────────────────────────────────────────────
const ThermalWrap = styled.div`
  width: 368px;
  min-height: 520px;
  margin: 0 auto;
  background: white;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  padding: 22px;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: ${p => p.fontSize === 'small' ? '9px' : p.fontSize === 'large' ? '12px' : '10px'};
  color: #111;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.10);
  display: flex;
  flex-direction: column;
  position: relative;
  &::before {
    content: 'A5 portrait';
    position: absolute;
    top: -22px;
    right: 0;
    font-size: 9px;
    color: #94a3b8;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.06em;
  }
`;

// ─── Invoice Live Preview Component ──────────────────────────────────────────
const InvoicePreview = ({ config: c, branding, logoPreview }) => {
  const { t } = useTranslation();
  const primary = branding?.primaryColor || '#10b981';
  const logoUrl = logoPreview;
  // Helper that picks a custom label, falling back to the built-in default.
  const L = (key) => {
    const custom = c.labels?.[key];
    if (typeof custom === 'string' && custom.trim()) return custom;
    return DEFAULT_INVOICE_LABELS[key] || '';
  };

  const renderSection = (id) => {
    switch (id) {
      case 'header':
        return (
          <InvHeaderWrap key="header" pos={c.headerLogoPosition} primary={primary}>
            {c.showLogo !== false && (
              <InvLogoBox primary={primary}>
                {logoUrl
                  ? <img src={logoUrl} alt="logo" />
                  : <Glasses size={18} color="white" />}
              </InvLogoBox>
            )}
            <div>
              <InvStoreName primary={primary}>{branding?.storeName || 'Store Name'}</InvStoreName>
              {c.showTagline && <InvTagline>{branding?.tagline || 'Your tagline here'}</InvTagline>}
              {c.showStorePhone && <InvTagline>{branding?.phone || '+213 555 000 000'}</InvTagline>}
              {c.showStoreAddress && <InvTagline>{branding?.city || 'City, Algeria'}</InvTagline>}
            </div>
          </InvHeaderWrap>
        );

      case 'meta':
        return (
          <InvMetaGrid key="meta">
            {c.showInvoiceNumber && <InvMetaItem><div className="lbl">{L('invoiceNumber')}</div><div className="val">{SAMPLE.orderNumber}</div></InvMetaItem>}
            {c.showDate && <InvMetaItem><div className="lbl">{L('date')}</div><div className="val">{SAMPLE.date}</div></InvMetaItem>}
            {c.showDueDate && <InvMetaItem><div className="lbl">{L('dueDate')}</div><div className="val">{SAMPLE.dueDate}</div></InvMetaItem>}
            {c.showStore && <InvMetaItem><div className="lbl">{L('store')}</div><div className="val">{branding?.storeName || 'Store Name'}</div></InvMetaItem>}
            {c.showSalesPerson && <InvMetaItem><div className="lbl">{L('salesPerson')}</div><div className="val">{SAMPLE.salesPerson}</div></InvMetaItem>}
            {c.showTechnician && <InvMetaItem><div className="lbl">{L('technician')}</div><div className="val">{SAMPLE.technician}</div></InvMetaItem>}
            {c.showStatus && <InvMetaItem><div className="lbl">{L('status')}</div><div className="val">confirmed</div></InvMetaItem>}
          </InvMetaGrid>
        );

      case 'client':
        return (
          <InvClientGrid key="client">
            {c.showClientName && <InvMetaItem><div className="lbl">{L('customer')}</div><div className="val">{SAMPLE.clientName}</div></InvMetaItem>}
            {c.showClientPhone && <InvMetaItem><div className="lbl">{L('phone')}</div><div className="val">{SAMPLE.clientPhone}</div></InvMetaItem>}
            {c.showClientBirthday && <InvMetaItem><div className="lbl">{L('birthday')}</div><div className="val">12/04/1990</div></InvMetaItem>}
            {c.showClientGenderAge && <InvMetaItem><div className="lbl">{L('genderAge')}</div><div className="val">{SAMPLE.clientGender} · {SAMPLE.clientAge}</div></InvMetaItem>}
            {c.showVisitNumber && <InvMetaItem><div className="lbl">{L('visitNumber')}</div><div className="val">{SAMPLE.visitNumber}</div></InvMetaItem>}
            {c.showFrame && <InvMetaItem><div className="lbl">{L('frame')}</div><div className="val">{t('branding.titaniumAxiom')}</div></InvMetaItem>}
            {c.showLens && <InvMetaItem><div className="lbl">{L('lens')}</div><div className="val">{t('branding.progressiveAr')}</div></InvMetaItem>}
            {c.showTimeToDeliver && <InvMetaItem><div className="lbl">{L('timeToDeliver')}</div><div className="val">{t('branding.x57BusinessDays')}</div></InvMetaItem>}
          </InvClientGrid>
        );

      case 'items':
        return (
          <InvTable key="items" primary={primary}>
            <thead>
              <tr>
                {c.showColItemName    && <th>{L('colDescription')}</th>}
                {c.showColLensType    && <th>{L('colLensType')}</th>}
                {c.showColSphCyl      && <th>{L('colSphCyl')}</th>}
                {c.showColQty         && <th>{L('colQty')}</th>}
                {c.showColUnitPrice   && <th>{L('colUnitPrice')}</th>}
                {c.showColTotal       && <th>{L('colTotal')}</th>}
              </tr>
            </thead>
            <tbody>
              {SAMPLE.items.map((item, i) => (
                <tr key={i}>
                  {c.showColItemName   && <td>{item.name}</td>}
                  {c.showColLensType   && <td>{item.lensType}</td>}
                  {c.showColSphCyl     && <td>{item.sph} / {item.cyl}</td>}
                  {c.showColQty        && <td>{item.qty}</td>}
                  {c.showColUnitPrice  && <td>{item.unitPrice.toLocaleString()} {t('branding.dzd')}</td>}
                  {c.showColTotal      && <td>{item.total.toLocaleString()} {t('branding.dzd')}</td>}
                </tr>
              ))}
              {c.showLensPrescription && (
                <tr>
                  <td colSpan={6} style={{ color: '#64748b', fontStyle: 'italic', fontSize: '9px' }}>
                    {L('rxPrefix')}{t('branding.odSph150Cyl')}
                  </td>
                </tr>
              )}
            </tbody>
          </InvTable>
        );

      case 'totals':
        return (
          <InvTotals key="totals" primary={primary}>
            {c.showSubtotal        && <div className="row"><span>{L('subtotal')}</span><span>{SAMPLE.subtotal.toLocaleString()} {t('branding.dzd')}</span></div>}
            {c.showDiscount        && <div className="row"><span>{L('discount')}</span><span>–{SAMPLE.discount.toLocaleString()} {t('branding.dzd')}</span></div>}
            <div className="row total"><span>{L('total')}</span><span>{SAMPLE.total.toLocaleString()} {t('branding.dzd')}</span></div>
            {c.showAmountPaid      && <div className="row"><span>{L('amountPaid')}</span><span>{SAMPLE.amountPaid.toLocaleString()} {t('branding.dzd')}</span></div>}
            {c.showRemainingBalance && <div className="row" style={{ color: '#dc2626' }}><span>{L('remaining')}</span><span>{SAMPLE.remaining.toLocaleString()} {t('branding.dzd')}</span></div>}
          </InvTotals>
        );

      case 'footer':
        return (
          <InvFooter key="footer" size={c.footerFontSize}>
            {c.footerMessage && <div style={{ marginBottom: 4, fontStyle: 'italic' }}>{c.footerMessage}</div>}
            {c.showFooterPhone   && <div>{branding?.phone || '+213 555 000 000'}</div>}
            {c.showFooterAddress && <div>{branding?.city || 'City, Algeria'}</div>}
            {c.showFooterWebsite && <div>{c.footerWebsite || 'www.optiva.dz'}</div>}
          </InvFooter>
        );

      default: return null;
    }
  };

  return (
    <InvPaper>
      {(c.sectionOrder || DEFAULT_INVOICE_TEMPLATE.sectionOrder).map(id => renderSection(id))}
    </InvPaper>
  );
};

// ─── Receipt Live Preview Component ──────────────────────────────────────────
const ReceiptPreview = ({ config: c, branding, logoPreview }) => {
  const { t } = useTranslation();
  const fsKey = c.fontSize || 'medium';
  const L = (key) => {
    const custom = c.labels?.[key];
    if (typeof custom === 'string' && custom.trim()) return custom;
    return DEFAULT_RECEIPT_LABELS[key] || '';
  };
  const primary = branding?.primaryColor || '#10b981';
  const storeName = branding?.storeName || 'Store Name';
  const storeMeta = [
    c.showPhone !== false ? (branding?.phone || '+213 555 000 000') : '',
    c.showEmail !== false ? (branding?.email || 'contact@store.dz') : '',
  ].filter(Boolean).join(' · ');
  const termsText = String(c.collectionTermsText || '').replace(/\{\{\s*store\s*\}\}/gi, storeName);
  const moneyRow = (label, value, variant) => (
    <div style={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 8,
      paddingBottom: 4,
      borderBottom: variant === 'total' ? '2px solid #0f172a' : variant === 'rest' ? 'none' : '1px dashed #e2e8f0',
      marginBottom: 4,
    }}>
      <span style={{ color: variant === 'rest' ? '#dc2626' : '#475569', fontWeight: 600 }}>{label}</span>
      <span style={{ fontWeight: 800, color: variant === 'rest' ? '#dc2626' : '#0f172a' }}>{value}</span>
    </div>
  );

  return (
    <ThermalWrap fontSize={fsKey}>
      {/* Header: logo | store | salesperson */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.8fr 1fr', alignItems: 'start', gap: 6 }}>
        <div>
          {c.showLogo && (
            logoPreview
              ? <img src={logoPreview} alt="logo" style={{ maxHeight: 46, maxWidth: 84, objectFit: 'contain' }} />
              : <div style={{ fontSize: 30 }}>👓</div>
          )}
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: c.storeNameSize === 'large' ? 16 : 13, fontWeight: 800, color: primary, lineHeight: 1.15 }}>{storeName}</div>
          {storeMeta && <div style={{ fontSize: '0.85em', color: '#475569', marginTop: 2 }}>{storeMeta}</div>}
          {c.showAddress !== false && <div style={{ fontSize: '0.85em', color: '#475569' }}>{branding?.city || 'City, Algeria'}</div>}
        </div>
        <div style={{ textAlign: 'right', fontSize: '0.85em', color: '#475569', lineHeight: 1.5 }}>
          {c.showSalesPerson !== false && <div style={{ fontWeight: 700, color: '#0f172a' }}>{SAMPLE.salesPerson}</div>}
          {c.showDate !== false && <><div>{SAMPLE.date}</div><div>17:05</div></>}
        </div>
      </div>

      <div style={{ borderTop: '2px solid #0f172a', margin: '6px 0' }} />
      {c.showTagline !== false && (
        <div style={{ textAlign: 'center', fontStyle: 'italic', color: '#475569' }}>
          {branding?.tagline || 'Your slogan here'}
        </div>
      )}
      <div style={{ borderTop: '2px solid #0f172a', margin: '6px 0' }} />

      <div style={{ textAlign: 'center', margin: '6px 0 4px' }}>
        {c.showCustomer !== false && (
          <>
            <div style={{ fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '.07em', color: '#94a3b8', fontWeight: 700 }}>{L('customer')}</div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{SAMPLE.clientName}</div>
          </>
        )}
        {c.showOrderNumber !== false && <div style={{ fontSize: '0.9em', fontWeight: 700, color: primary }}>{SAMPLE.orderNumber}</div>}
      </div>

      <div style={{ marginTop: 6, flex: 1 }}>
        <div>
          {c.showTimeToDeliver !== false && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', fontWeight: 800 }}>{L('timeToDeliver')}</div>
              <div style={{ fontWeight: 700 }}>{t('branding.x57Days')}</div>
            </div>
          )}
          {c.showCorrection !== false && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', fontWeight: 800 }}>{L('correction')}</div>
              <div style={{ fontWeight: 700, lineHeight: 1.5 }}><span style={{ color: '#64748b', marginRight: 4 }}>{t('branding.od')}</span>{t('branding.sph125Cyl0')}</div>
              <div style={{ fontWeight: 700, lineHeight: 1.5 }}><span style={{ color: '#64748b', marginRight: 4 }}>{t('branding.os')}</span>{t('branding.sph100Cyl0')}</div>
            </div>
          )}
          {c.showFrame && (
            <div style={{ marginBottom: 8 }}>
              <div style={{ fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', fontWeight: 800 }}>{L('frame')}</div>
              <div style={{ fontWeight: 700 }}>{t('branding.titaniumAxiom2')}</div>
            </div>
          )}
          {c.showLens && (
            <div>
              <div style={{ fontSize: '0.72em', textTransform: 'uppercase', letterSpacing: '.06em', color: '#94a3b8', fontWeight: 800 }}>{L('lens')}</div>
              <div style={{ fontWeight: 700 }}>{t('branding.progressiveAr')}</div>
            </div>
          )}
        </div>

        <div style={{ marginTop: 12 }}>
          {c.showPayment !== false && moneyRow(L('payment'), 'cash')}
          {c.showSubtotal && moneyRow(L('subtotal'), `${SAMPLE.subtotal.toLocaleString()} DZD`)}
          {c.showDiscount && moneyRow(L('discount'), `–${SAMPLE.discount.toLocaleString()} DZD`)}
          {c.showAmountPaid !== false && moneyRow(L('amountPaid'), `${SAMPLE.amountPaid.toLocaleString()} DZD`)}
          {c.showTotal !== false && moneyRow(L('total'), `${SAMPLE.total.toLocaleString()} DZD`, 'total')}
          {c.showRemainingBalance !== false && moneyRow(L('remaining'), `${SAMPLE.remaining.toLocaleString()} DZD`, 'rest')}
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'flex-end', gap: 10, marginTop: 8 }}>
        {c.showQR !== false && (
          <div style={{ width: 48, height: 48, background: 'repeating-conic-gradient(#000 0% 25%, #fff 0% 50%) 50% / 6px 6px', borderRadius: 3 }} />
        )}
        {c.showStampBox !== false && (
          <div style={{
            width: 104, height: 48, border: '1.5px dashed #94a3b8', borderRadius: 8,
            padding: '4px 6px', color: '#94a3b8', fontSize: '0.72em', fontWeight: 800,
            textTransform: 'uppercase', letterSpacing: '.05em',
          }}>{L('stamp')}</div>
        )}
      </div>

      <div style={{ marginTop: 8, borderTop: '1px solid #e2e8f0', paddingTop: 6, textAlign: 'center' }}>
        {c.footerMessage && <div style={{ fontWeight: 800, color: primary }}>{c.footerMessage}</div>}
        {c.showCollectionTerms !== false && termsText && (
          <div style={{ fontSize: '0.85em', color: '#64748b', lineHeight: 1.5, marginTop: 3 }}>{termsText}</div>
        )}
        {c.showReturnPolicy && <div style={{ fontSize: '0.85em', color: '#64748b', marginTop: 3 }}>{c.returnPolicyText}</div>}
      </div>
    </ThermalWrap>
  );
};

// ─── Invoice Builder Panel (left side) ───────────────────────────────────────
const InvoiceBuilderPanel = ({ config, onChange, sectionOrder, onReorder }) => {
  const { t } = useTranslation();
  const [openSections, setOpenSections] = useState({ header: true });
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const dragSrc = useRef(null);

  const toggleSection = (id) => setOpenSections(prev => ({ ...prev, [id]: !prev[id] }));
  const upd = (field, val) => onChange({ ...config, [field]: val });

  const dnd = {
    onDragStart: (e, idx) => { dragSrc.current = idx; e.dataTransfer.effectAllowed = 'move'; },
    onDragEnd:   ()        => { setDragOverIdx(null); },
    onDragOver:  (e, idx)  => { e.preventDefault(); setDragOverIdx(idx); },
    onDrop:      (e, idx)  => {
      e.preventDefault();
      const from = dragSrc.current;
      if (from === null || from === idx) { setDragOverIdx(null); return; }
      const next = [...sectionOrder];
      const [moved] = next.splice(from, 1);
      next.splice(idx, 0, moved);
      onReorder(next);
      dragSrc.current = null;
      setDragOverIdx(null);
    },
  };

  const renderBody = (id) => {
    switch (id) {
      case 'header':
        return (
          <>
            <SubLabel>{t('branding.logoPosition')}</SubLabel>
            <RadioRow>
              {[
                { val: 'left',   icon: <AlignLeft size={12} /> },
                { val: 'center', icon: <AlignCenter size={12} /> },
                { val: 'right',  icon: <AlignRight size={12} /> },
              ].map(({ val, icon }) => (
                <RadioChip key={val} active={config.headerLogoPosition === val}>
                  <input type="radio" checked={config.headerLogoPosition === val} onChange={() => upd('headerLogoPosition', val)} />
                  {icon} {val}
                </RadioChip>
              ))}
            </RadioRow>
            <SubLabel>{t('branding.showHide')}</SubLabel>
            <Toggle label={t('branding.logo')}          checked={config.showLogo}         onChange={() => upd('showLogo',         !config.showLogo)} />
            <Toggle label={t('branding.tagline')}       checked={config.showTagline}      onChange={() => upd('showTagline',      !config.showTagline)} />
            <Toggle label={t('branding.storePhone')}   checked={config.showStorePhone}   onChange={() => upd('showStorePhone',   !config.showStorePhone)} />
            <Toggle label={t('branding.storeAddress')} checked={config.showStoreAddress} onChange={() => upd('showStoreAddress', !config.showStoreAddress)} />
            <SubLabel>{t('branding.editText')}</SubLabel>
            <LabelEditor
              entries={[
                ['heading',   'Heading'],
                ['badge',     'Badge'],
                ['pageTitle', 'Page title'],
              ]}
              labels={config.labels}
              defaults={DEFAULT_INVOICE_LABELS}
              onChange={(next) => upd('labels', next)}
            />
          </>
        );
      case 'meta':
        return (
          <>
            <Toggle label={t('branding.invoiceNumber')} checked={config.showInvoiceNumber} onChange={() => upd('showInvoiceNumber', !config.showInvoiceNumber)} />
            <Toggle label={t('branding.date')}           checked={config.showDate}          onChange={() => upd('showDate',          !config.showDate)} />
            <Toggle label={t('branding.dueDate')}       checked={config.showDueDate}       onChange={() => upd('showDueDate',       !config.showDueDate)} />
            <Toggle label={t('branding.storeLabel')}          checked={config.showStore}         onChange={() => upd('showStore',         !config.showStore)} />
            <Toggle label={t('branding.salesPerson')}   checked={config.showSalesPerson}   onChange={() => upd('showSalesPerson',   !config.showSalesPerson)} />
            <Toggle label={t('branding.technician')}     checked={config.showTechnician}    onChange={() => upd('showTechnician',    !config.showTechnician)} />
            <Toggle label={t('branding.status')}         checked={config.showStatus}        onChange={() => upd('showStatus',        !config.showStatus)} />
            <Toggle label={t('branding.qrCode')}        checked={config.showQR}            onChange={() => upd('showQR',            !config.showQR)} />
            <SubLabel>{t('branding.editText')}</SubLabel>
            <LabelEditor
              entries={[
                ['invoiceNumber', 'Invoice #'],
                ['date',          'Date'],
                ['dueDate',       'Due date'],
                ['store',         'Store'],
                ['salesPerson',   'Sales person'],
                ['technician',    'Technician'],
                ['status',        'Status'],
              ]}
              labels={config.labels}
              defaults={DEFAULT_INVOICE_LABELS}
              onChange={(next) => upd('labels', next)}
            />
          </>
        );
      case 'client':
        return (
          <>
            <Toggle label={t('branding.clientName')}   checked={config.showClientName}      onChange={() => upd('showClientName',      !config.showClientName)} />
            <Toggle label={t('branding.clientPhone')}  checked={config.showClientPhone}     onChange={() => upd('showClientPhone',     !config.showClientPhone)} />
            <Toggle label={t('branding.birthday')}      checked={config.showClientBirthday}  onChange={() => upd('showClientBirthday',  !config.showClientBirthday)} />
            <Toggle label={t('branding.genderAge')}  checked={config.showClientGenderAge} onChange={() => upd('showClientGenderAge', !config.showClientGenderAge)} />
            <Toggle label={t('branding.visitNumber')}  checked={config.showVisitNumber}     onChange={() => upd('showVisitNumber',     !config.showVisitNumber)} />
            <Toggle label={t('branding.frame')}         checked={config.showFrame}           onChange={() => upd('showFrame',           !config.showFrame)} />
            <Toggle label={t('branding.lens')}          checked={config.showLens}            onChange={() => upd('showLens',            !config.showLens)} />
            <Toggle label={t('branding.timeToDeliver')} checked={config.showTimeToDeliver} onChange={() => upd('showTimeToDeliver',   !config.showTimeToDeliver)} />
            <SubLabel>{t('branding.editText')}</SubLabel>
            <LabelEditor
              entries={[
                ['customer',      'Customer'],
                ['phone',         'Phone'],
                ['birthday',      'Birthday'],
                ['genderAge',     'Gender / Age'],
                ['visitNumber',   'Visit #'],
                ['frame',         'Frame'],
                ['lens',          'Lens'],
                ['timeToDeliver', 'Time to deliver'],
              ]}
              labels={config.labels}
              defaults={DEFAULT_INVOICE_LABELS}
              onChange={(next) => upd('labels', next)}
            />
          </>
        );
      case 'items':
        return (
          <>
            <SubLabel>{t('branding.columns')}</SubLabel>
            <Toggle label={t('branding.itemName')}       checked={config.showColItemName}   onChange={() => upd('showColItemName',   !config.showColItemName)} />
            <Toggle label={t('branding.lensType')}       checked={config.showColLensType}   onChange={() => upd('showColLensType',   !config.showColLensType)} />
            <Toggle label={t('branding.sphCyl')}       checked={config.showColSphCyl}     onChange={() => upd('showColSphCyl',     !config.showColSphCyl)} />
            <Toggle label={t('branding.qty')}             checked={config.showColQty}        onChange={() => upd('showColQty',        !config.showColQty)} />
            <Toggle label={t('branding.unitPrice')}      checked={config.showColUnitPrice}  onChange={() => upd('showColUnitPrice',  !config.showColUnitPrice)} />
            <Toggle label={t('branding.total')}           checked={config.showColTotal}      onChange={() => upd('showColTotal',      !config.showColTotal)} />
            <SubLabel>{t('branding.extras')}</SubLabel>
            <Toggle label={t('branding.lensPrescriptionRow')} checked={config.showLensPrescription} onChange={() => upd('showLensPrescription', !config.showLensPrescription)} />
            <Toggle label={t('branding.showMeasurements')}     checked={config.showMeasurements}     onChange={() => upd('showMeasurements',     !config.showMeasurements)} />
            <SubLabel>{t('branding.editText')}</SubLabel>
            <LabelEditor
              entries={[
                ['itemsHeading',        'Items heading'],
                ['measurementsHeading', 'Measurements heading'],
                ['colDescription',      'Description'],
                ['colLensType',         'Lens type'],
                ['colSphCyl',           'SPH / CYL'],
                ['colQty',              'Qty'],
                ['colUnitPrice',        'Unit price'],
                ['colTotal',            'Amount'],
                ['rxPrefix',            'Rx prefix'],
              ]}
              labels={config.labels}
              defaults={DEFAULT_INVOICE_LABELS}
              onChange={(next) => upd('labels', next)}
            />
          </>
        );
      case 'totals':
        return (
          <>
            <Toggle label={t('branding.subtotal')}          checked={config.showSubtotal}         onChange={() => upd('showSubtotal',         !config.showSubtotal)} />
            <Toggle label={t('branding.discount')}          checked={config.showDiscount}         onChange={() => upd('showDiscount',         !config.showDiscount)} />
            <Toggle label={t('branding.amountPaid')}       checked={config.showAmountPaid}       onChange={() => upd('showAmountPaid',       !config.showAmountPaid)} />
            <Toggle label={t('branding.remainingBalance')} checked={config.showRemainingBalance} onChange={() => upd('showRemainingBalance', !config.showRemainingBalance)} />
            <SubLabel>{t('branding.editText')}</SubLabel>
            <LabelEditor
              entries={[
                ['subtotal',   'Subtotal'],
                ['discount',   'Discount'],
                ['total',      'Total'],
                ['amountPaid', 'Paid'],
                ['remaining',  'Remaining'],
              ]}
              labels={config.labels}
              defaults={DEFAULT_INVOICE_LABELS}
              onChange={(next) => upd('labels', next)}
            />
          </>
        );
      case 'footer':
        return (
          <>
            <SubLabel>{t('branding.customMessage')}</SubLabel>
            <TextArea
              value={config.footerMessage}
              onChange={e => upd('footerMessage', e.target.value)}
              placeholder={t('branding.thankYouForYourVisit')}
            />
            <Toggle label={t('branding.showPhone')}   checked={config.showFooterPhone}   onChange={() => upd('showFooterPhone',   !config.showFooterPhone)} />
            <Toggle label={t('branding.showAddress')} checked={config.showFooterAddress} onChange={() => upd('showFooterAddress', !config.showFooterAddress)} />
            <Toggle label={t('branding.showWebsite')} checked={config.showFooterWebsite} onChange={() => upd('showFooterWebsite', !config.showFooterWebsite)} />
            {config.showFooterWebsite && (
              <>
                <SubLabel>{t('branding.websiteUrl')}</SubLabel>
                <TextInput
                  value={config.footerWebsite || ''}
                  onChange={e => upd('footerWebsite', e.target.value)}
                  placeholder={t('branding.wwwOptivaDz')}
                />
              </>
            )}
            <SubLabel>{t('branding.footerFontSize')}</SubLabel>
            <RadioRow>
              {['small', 'medium', 'large'].map(s => (
                <SizeChip key={s} active={config.footerFontSize === s}>
                  <input type="radio" checked={config.footerFontSize === s} onChange={() => upd('footerFontSize', s)} />
                  {s}
                </SizeChip>
              ))}
            </RadioRow>
          </>
        );
      default: return null;
    }
  };

  return (
    <div>
      {sectionOrder.map((id, idx) => {
        const meta = SECTIONS[id];
        if (!meta) return null;
        return (
          <DragRow
            key={id}
            draggable
            dragOver={dragOverIdx === idx}
            onDragStart={e => dnd.onDragStart(e, idx)}
            onDragEnd={dnd.onDragEnd}
            onDragOver={e => dnd.onDragOver(e, idx)}
            onDrop={e => dnd.onDrop(e, idx)}
          >
            <DragRowHead onClick={() => toggleSection(id)}>
              <DragHandle onClick={e => e.stopPropagation()}>
                <GripVertical size={14} />
              </DragHandle>
              <DragRowLabel>{meta.label}</DragRowLabel>
              <ExpandIcon open={openSections[id]}>
                <ChevronDown size={14} />
              </ExpandIcon>
            </DragRowHead>
            <DragRowBody open={openSections[id]}>
              {renderBody(id)}
            </DragRowBody>
          </DragRow>
        );
      })}
    </div>
  );
};

// ─── Receipt Builder Panel ────────────────────────────────────────────────────
const ReceiptBuilderPanel = ({ config, onChange }) => {
  const { t } = useTranslation();
  const upd = (field, val) => onChange({ ...config, [field]: val });
  const updLabels = (next) => upd('labels', next);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      {/* ── Header ─────────────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><AlignCenter size={15} /> {t('branding.header')}</CardHead>
        <CardBody>
          <Toggle label={t('branding.showLogo')}    checked={config.showLogo}    onChange={() => upd('showLogo',    !config.showLogo)} />
          <Toggle label={t('branding.showSlogan')}  checked={config.showTagline !== false} onChange={() => upd('showTagline', config.showTagline === false)} />
          <Toggle label={t('branding.showPhone')}   checked={config.showPhone !== false}   onChange={() => upd('showPhone',   config.showPhone === false)} />
          <Toggle label={t('branding.showEmail')}   checked={config.showEmail !== false}   onChange={() => upd('showEmail',   config.showEmail === false)} />
          <Toggle label={t('branding.showAddress')} checked={config.showAddress !== false} onChange={() => upd('showAddress', config.showAddress === false)} />
          <SubLabel>{t('branding.storeNameSize')}</SubLabel>
          <RadioRow>
            {['large', 'medium'].map(s => (
              <SizeChip key={s} active={config.storeNameSize === s}>
                <input type="radio" checked={config.storeNameSize === s} onChange={() => upd('storeNameSize', s)} />
                {s}
              </SizeChip>
            ))}
          </RadioRow>
          <SubLabel>{t('branding.editText')}</SubLabel>
          <LabelEditor
            entries={[
              ['heading',   'Heading'],
              ['badge',     'Badge'],
              ['pageTitle', 'Page title'],
            ]}
            labels={config.labels}
            defaults={DEFAULT_RECEIPT_LABELS}
            onChange={updLabels}
          />
        </CardBody>
      </SectionCard>

      {/* ── Order info ─────────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><FileText size={15} /> {t('branding.orderInfo')}</CardHead>
        <CardBody>
          <Toggle label={t('branding.orderNumber')}        checked={config.showOrderNumber !== false} onChange={() => upd('showOrderNumber', config.showOrderNumber === false)} />
          <Toggle label={t('branding.dateAndTime')}       checked={config.showDate !== false}        onChange={() => upd('showDate',        config.showDate === false)} />
          <Toggle label={t('branding.salesPerson')}        checked={config.showSalesPerson !== false} onChange={() => upd('showSalesPerson', config.showSalesPerson === false)} />
          <Toggle label={t('branding.qrCode')}             checked={config.showQR !== false}          onChange={() => upd('showQR',          config.showQR === false)} />
          <Toggle label={t('branding.stampBox')}           checked={config.showStampBox !== false}    onChange={() => upd('showStampBox',    config.showStampBox === false)} />
          <SubLabel>{t('branding.editText')}</SubLabel>
          <LabelEditor
            entries={[
              ['orderNumber', 'Order number'],
              ['date',        'Date'],
              ['salesPerson', 'Sales person'],
              ['stamp',       'Stamp'],
            ]}
            labels={config.labels}
            defaults={DEFAULT_RECEIPT_LABELS}
            onChange={updLabels}
          />
        </CardBody>
      </SectionCard>

      {/* ── Customer info ──────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><Glasses size={15} /> {t('branding.customerInfo')}</CardHead>
        <CardBody>
          <Toggle label={t('branding.customerName')}   checked={config.showCustomer !== false}         onChange={() => upd('showCustomer',         config.showCustomer === false)} />
          <Toggle label={t('branding.correction')}      checked={config.showCorrection !== false}       onChange={() => upd('showCorrection',       config.showCorrection === false)} />
          <Toggle label={t('branding.timeToDeliver')} checked={config.showTimeToDeliver !== false}    onChange={() => upd('showTimeToDeliver',    config.showTimeToDeliver === false)} />
          <Toggle label={t('branding.frame')}           checked={!!config.showFrame}                    onChange={() => upd('showFrame',            !config.showFrame)} />
          <Toggle label={t('branding.lens')}            checked={!!config.showLens}                     onChange={() => upd('showLens',             !config.showLens)} />
          <SubLabel>{t('branding.editText')}</SubLabel>
          <LabelEditor
            entries={[
              ['customer',      'Customer'],
              ['correction',    'Correction'],
              ['timeToDeliver', 'Time to deliver'],
              ['frame',         'Frame'],
              ['lens',          'Lens'],
            ]}
            labels={config.labels}
            defaults={DEFAULT_RECEIPT_LABELS}
            onChange={updLabels}
          />
        </CardBody>
      </SectionCard>

      {/* ── Items / totals ─────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><Receipt size={15} /> {t('branding.paymentTotals')}</CardHead>
        <CardBody>
          <Toggle label={t('branding.paymentMethod')}    checked={config.showPayment !== false}      onChange={() => upd('showPayment',          config.showPayment === false)} />
          <Toggle label={t('branding.advancePayment')}   checked={config.showAmountPaid !== false}   onChange={() => upd('showAmountPaid',       config.showAmountPaid === false)} />
          <Toggle label={t('branding.subtotal')}          checked={!!config.showSubtotal}             onChange={() => upd('showSubtotal',         !config.showSubtotal)} />
          <Toggle label={t('branding.discount')}          checked={!!config.showDiscount}             onChange={() => upd('showDiscount',         !config.showDiscount)} />
          <Toggle label={t('branding.total')}             checked={config.showTotal !== false}        onChange={() => upd('showTotal',            config.showTotal === false)} />
          <Toggle label={t('branding.restToPay')}       checked={config.showRemainingBalance !== false} onChange={() => upd('showRemainingBalance', config.showRemainingBalance === false)} />
          <SubLabel>{t('branding.editText')}</SubLabel>
          <LabelEditor
            entries={[
              ['payment',    'Payment'],
              ['subtotal',   'Subtotal'],
              ['discount',   'Discount'],
              ['amountPaid', 'Advance payment'],
              ['total',      'Total'],
              ['remaining',  'Rest to pay'],
            ]}
            labels={config.labels}
            defaults={DEFAULT_RECEIPT_LABELS}
            onChange={updLabels}
          />
        </CardBody>
      </SectionCard>

      {/* ── Footer ─────────────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><FileText size={15} /> {t('branding.footer')}</CardHead>
        <CardBody>
          <SubLabel>{t('branding.thankYouMessage')}</SubLabel>
          <TextArea value={config.footerMessage} onChange={e => upd('footerMessage', e.target.value)} placeholder={t('branding.thankYouForYourVisit')} />
          <Toggle
            label={t('branding.showCollectionTerms')}
            checked={config.showCollectionTerms !== false}
            onChange={() => upd('showCollectionTerms', config.showCollectionTerms === false)}
          />
          {config.showCollectionTerms !== false && (
            <>
              <TextArea
                value={config.collectionTermsText}
                onChange={e => upd('collectionTermsText', e.target.value)}
                placeholder={t('branding.theEquipmentMustBeCollected')}
              />
              <SubLabel>{t('branding.type')} {t('branding.store')} {t('branding.anywhereInTheTextTo')}</SubLabel>
            </>
          )}
          <Toggle label={t('branding.showReturnPolicy')} checked={config.showReturnPolicy} onChange={() => upd('showReturnPolicy', !config.showReturnPolicy)} />
          {config.showReturnPolicy && (
            <TextArea value={config.returnPolicyText} onChange={e => upd('returnPolicyText', e.target.value)} placeholder={t('branding.returnsAcceptedWithin7Days')} />
          )}
        </CardBody>
      </SectionCard>

      {/* ── Print settings ─────────────────────────────────────────────── */}
      <SectionCard>
        <CardHead><Palette size={15} /> {t('branding.printSettings')}</CardHead>
        <CardBody>
          <SubLabel>{t('branding.fontSizeA5Receipt')}</SubLabel>
          <RadioRow>
            {['small', 'medium', 'large'].map(s => (
              <SizeChip key={s} active={config.fontSize === s}>
                <input type="radio" checked={config.fontSize === s} onChange={() => upd('fontSize', s)} />
                {s}
              </SizeChip>
            ))}
          </RadioRow>
        </CardBody>
      </SectionCard>
    </div>
  );
};

// ─── Order numbering panel (new tab) ─────────────────────────────────────────
const FormatTokenChip = styled.button`
  padding: 0.25rem 0.625rem;
  border-radius: 6px;
  border: 1px solid ${T.hairline};
  background: ${T.surface};
  color: ${T.body};
  font-family: monospace;
  font-size: 0.75rem;
  cursor: pointer;
  transition: all 0.15s;
  &:hover { border-color: ${T.brand}; color: ${T.brand}; background: var(--c-brand-soft); }
`;

const PreviewBox = styled.div`
  background: ${T.soft};
  border: 1px dashed ${T.hairline};
  border-radius: 8px;
  padding: 0.75rem 1rem;
  font-family: monospace;
  font-size: 1.05rem;
  font-weight: 600;
  color: ${T.brand};
  word-break: break-all;
`;

const OrderNumberingPanel = ({ format, onFormatChange, cityCode, onCityCodeChange, storeCode, onStoreCodeChange }) => {
  const { t } = useTranslation();
  const insertToken = (token) => {
    onFormatChange((format || DEFAULT_ORDER_NUMBER_FORMAT) + token);
  };
  const tokens = [
    { t: '{YY}',    h: '2-digit year (26)' },
    { t: '{YYYY}',  h: '4-digit year (2026)' },
    { t: '{MM}',    h: 'month (05)' },
    { t: '{DD}',    h: 'day (08)' },
    { t: '{CITY}',  h: 'city code (12)' },
    { t: '{STORE}', h: 'store code (01)' },
    { t: '{DAYSEQ3}', h: 'order sequence that day padded to 3' },
    { t: '{DAYSEQ4}', h: 'order sequence that day padded to 4' },
    { t: '{SEQ6}',    h: 'global sequence padded to 6' },
  ];
  const preview = previewOrderNumber(format || DEFAULT_ORDER_NUMBER_FORMAT, {
    cityCode, storeCode, daySequence: 1, sequence: 1,
  });
  const previewSeq42 = previewOrderNumber(format || DEFAULT_ORDER_NUMBER_FORMAT, {
    cityCode, storeCode, daySequence: 42, sequence: 42,
  });
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
      <SectionCard>
        <CardHead><Hash size={15} /> {t('branding.formatTemplate')}</CardHead>
        <CardBody>
          <SubLabel>{t('branding.orderNumberPattern')}</SubLabel>
          <TextInput
            value={format || ''}
            onChange={e => onFormatChange(e.target.value)}
            placeholder={DEFAULT_ORDER_NUMBER_FORMAT}
            style={{ fontFamily: 'monospace', fontSize: '0.95rem' }}
          />
          <SubLabel>{t('branding.insertToken')}</SubLabel>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem' }}>
            {tokens.map(({ t, h }) => (
              <FormatTokenChip key={t} type="button" title={h} onClick={() => insertToken(t)}>{t}</FormatTokenChip>
            ))}
          </div>
          <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: T.muted }}>
            {t('branding.tipLeaveEmptyToUse')} <code>{DEFAULT_ORDER_NUMBER_FORMAT}</code>.
          </div>
        </CardBody>
      </SectionCard>

      <SectionCard>
        <CardHead><Tag size={15} /> {t('branding.codes')}</CardHead>
        <CardBody>
          <FieldGrid>
            <FieldGroup>
              <FieldLabel>{t('branding.cityCode2Digits')}</FieldLabel>
              <TextInput
                value={cityCode || ''}
                onChange={e => onCityCodeChange(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="12"
                inputMode="numeric"
                maxLength={2}
                readOnly
                style={{ background: 'var(--c-surface-soft)', cursor: 'not-allowed' }}
              />
            </FieldGroup>
            <FieldGroup>
              <FieldLabel>{t('branding.storeCode2Digits')}</FieldLabel>
              <TextInput
                value={storeCode || ''}
                onChange={e => onStoreCodeChange(e.target.value.replace(/\D/g, '').slice(0, 2))}
                placeholder="01"
                inputMode="numeric"
                maxLength={2}
                readOnly
                style={{ background: 'var(--c-surface-soft)', cursor: 'not-allowed' }}
              />
            </FieldGroup>
          </FieldGrid>
          <div style={{ marginTop: '0.5rem', fontSize: '0.72rem', color: T.muted }}>
            {t('branding.theseAreAssignedWhenAdding')} <code>{t('branding.city')}</code> and <code>{t('branding.store2')}</code> {t('branding.tokensAbove')}
          </div>
        </CardBody>
      </SectionCard>

      <SectionCard>
        <CardHead><Eye size={15} /> {t('branding.livePreview')}</CardHead>
        <CardBody>
          <PreviewBox>{preview}</PreviewBox>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: '0.5rem', fontSize: '0.78rem', color: T.muted }}>
            {t('branding.withTodaySSequence42')}
            <code style={{ color: T.body, fontWeight: 600 }}>{previewSeq42}</code>
          </div>
        </CardBody>
      </SectionCard>
    </div>
  );
};

// ─── Two-column builder layout ────────────────────────────────────────────────
const BuilderShell = styled.div`
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 1.25rem;
  align-items: start;
  @media (max-width: 900px) { grid-template-columns: 1fr; }
`;

const BuilderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0;
  max-height: 620px;
  overflow-y: auto;
  padding-right: 2px;
`;

const BuilderRight = styled.div`
  position: sticky;
  top: 0;
  overflow-y: auto;
  max-height: 680px;
`;

const PreviewLabel = styled.div`
  font-size: 0.75rem;
  font-weight: 600;
  color: ${T.muted};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.5rem;
`;

// ─── Main Component ───────────────────────────────────────────────────────────
const StoreBrandingSettings = () => {
  const { t } = useTranslation();
  const { refreshBranding } = useBranding();
  const [stores,        setStores]        = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [selected,      setSelected]      = useState(null);
  const [activeTab,     setActiveTab]     = useState('branding');
  const [form,          setForm]          = useState({
    storeName:    '',
    tagline:      '',
    primaryColor: DEFAULT_BRANDING.primaryColor,
    accentColor:  DEFAULT_BRANDING.accentColor,
    cityCode:     '',
    storeCode:    '',
  });
  const [logoFile,      setLogoFile]      = useState(null);
  const [logoPreview,   setLogoPreview]   = useState(null);
  const [removeLogo,    setRemoveLogo]    = useState(false);
  const [drag,          setDrag]          = useState(false);
  const [saving,        setSaving]        = useState(false);
  const [feedback,      setFeedback]      = useState(null);
  const [invoiceTpl,    setInvoiceTpl]    = useState({ ...DEFAULT_INVOICE_TEMPLATE });
  const [receiptTpl,    setReceiptTpl]    = useState({ ...DEFAULT_RECEIPT_TEMPLATE });
  const [sectionOrder,  setSectionOrder]  = useState([...DEFAULT_INVOICE_TEMPLATE.sectionOrder]);
  const [orderNumberFormat, setOrderNumberFormat] = useState(DEFAULT_ORDER_NUMBER_FORMAT);

  const fileRef = useRef(null);
  const token   = () => localStorage.getItem('token');

  const fetchStores = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/api/stores', { headers: { Authorization: `Bearer ${token()}` } });
      setStores((data.stores || []).filter(s => !s.isCentralWarehouse));
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchStores(); }, [fetchStores]);

  const selectStore = (store) => {
    setSelected(store);
    setFeedback(null);
    setLogoFile(null);
    setRemoveLogo(false);
    setForm({
      storeName:    store.name || '',
      tagline:      store.branding?.tagline       || '',
      primaryColor: store.branding?.primaryColor  || DEFAULT_BRANDING.primaryColor,
      accentColor:  store.branding?.accentColor   || DEFAULT_BRANDING.accentColor,
      cityCode:     store.cityCode || store.branding?.cityCode || '',
      storeCode:    store.storeCode || '',
    });
    const iTpl = {
      ...DEFAULT_INVOICE_TEMPLATE,
      ...(store.branding?.invoiceTemplate || {}),
      labels: { ...DEFAULT_INVOICE_LABELS, ...(store.branding?.invoiceTemplate?.labels || {}) },
    };
    const rTpl = {
      ...DEFAULT_RECEIPT_TEMPLATE,
      ...(store.branding?.receiptTemplate || {}),
      labels: { ...DEFAULT_RECEIPT_LABELS, ...(store.branding?.receiptTemplate?.labels || {}) },
    };
    setInvoiceTpl(iTpl);
    setReceiptTpl(rTpl);
    setSectionOrder(iTpl.sectionOrder || [...DEFAULT_INVOICE_TEMPLATE.sectionOrder]);
    setOrderNumberFormat(store.branding?.orderNumberFormat || DEFAULT_ORDER_NUMBER_FORMAT);
    setLogoPreview(store.logo ? (store.logo.startsWith('http') ? store.logo : `/${store.logo}`) : null);
  };

  const applyLogoFile = (file) => {
    if (!file) return;
    setLogoFile(file);
    setRemoveLogo(false);
    const reader = new FileReader();
    reader.onload = e => setLogoPreview(e.target.result);
    reader.readAsDataURL(file);
  };

  const handleLogoInput = e => applyLogoFile(e.target.files?.[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    const f = e.dataTransfer.files?.[0];
    if (f?.type.startsWith('image/')) applyLogoFile(f);
  };

  const handleReset = () => {
    if (!selected) return;
    setForm({
      storeName:    selected.name || '',
      tagline:      '',
      primaryColor: DEFAULT_BRANDING.primaryColor,
      accentColor:  DEFAULT_BRANDING.accentColor,
      cityCode:     '',
      storeCode:    selected.storeCode || '',
    });
    setLogoFile(null); setLogoPreview(null); setRemoveLogo(true);
    setInvoiceTpl({ ...DEFAULT_INVOICE_TEMPLATE, labels: { ...DEFAULT_INVOICE_LABELS } });
    setReceiptTpl({ ...DEFAULT_RECEIPT_TEMPLATE, labels: { ...DEFAULT_RECEIPT_LABELS } });
    setSectionOrder([...DEFAULT_INVOICE_TEMPLATE.sectionOrder]);
    setOrderNumberFormat(DEFAULT_ORDER_NUMBER_FORMAT);
    setFeedback(null);
  };

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    setFeedback(null);
    try {
      const fd = new FormData();
      fd.append('storeName',         form.storeName);
      fd.append('tagline',           form.tagline);
      fd.append('primaryColor',      form.primaryColor);
      fd.append('accentColor',       form.accentColor);
      fd.append('cityCode',          form.cityCode || '');
      fd.append('orderNumberFormat', orderNumberFormat || DEFAULT_ORDER_NUMBER_FORMAT);
      fd.append('invoiceTemplate', JSON.stringify({ ...invoiceTpl, sectionOrder }));
      fd.append('receiptTemplate', JSON.stringify(receiptTpl));
      if (logoFile)   fd.append('logo', logoFile);
      if (removeLogo) fd.append('removeLogo', 'true');

      const { data } = await axios.put(
        `/api/stores/${selected._id}/branding`, fd,
        { headers: { Authorization: `Bearer ${token()}`, 'Content-Type': 'multipart/form-data' } }
      );

      setFeedback({ ok: true, msg: 'All settings saved.' });
      setLogoFile(null); setRemoveLogo(false);
      await fetchStores();
      setSelected(prev => prev ? { ...prev,
        name: data.branding.storeName,
        logo: data.branding.logo,
        cityCode: data.branding.cityCode,
        storeCode: data.branding.storeCode,
        branding: {
          ...prev.branding,
          primaryColor:      data.branding.primaryColor,
          accentColor:       data.branding.accentColor,
          tagline:           data.branding.tagline,
          invoiceTemplate:   data.branding.invoiceTemplate,
          receiptTemplate:   data.branding.receiptTemplate,
          orderNumberFormat: data.branding.orderNumberFormat,
        },
      } : prev);
      if (data.branding.logo) setLogoPreview(`/${data.branding.logo}`);
      else if (removeLogo)    setLogoPreview(null);

      // Push the freshest payload into the BrandingContext / localStorage so
      // the admin's own session immediately uses the new template/labels.
      // Other logged-in sessions pick it up on next refreshBranding cycle.
      try { await refreshBranding(selected._id); } catch (_) {}
    } catch (e) {
      setFeedback({ ok: false, msg: e.response?.data?.message || 'Save failed.' });
    } finally {
      setSaving(false);
    }
  };

  const hasBranding = s => !!(s.branding?.primaryColor || s.branding?.tagline || s.logo);

  // ── Inline branding preview (reused in branding tab) ─────────────────────────
  const PreviewCard = () => (
    <PreviewShell>
      <PreviewHeader primary={form.primaryColor}>
        <div style={{ width: 32, height: 32, borderRadius: 7, background: 'rgba(255,255,255,0.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', flexShrink: 0 }}>
          {logoPreview ? <img src={logoPreview} alt="logo" style={{ width: '100%', height: '100%', objectFit: 'contain' }} /> : <Glasses size={16} color="rgba(255,255,255,0.8)" />}
        </div>
        <div>
          <div style={{ fontWeight: 700, color: 'white', fontSize: '0.875rem' }}>{form.storeName || 'Store Name'}</div>
          {form.tagline && <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.8)' }}>{form.tagline}</div>}
        </div>
      </PreviewHeader>
      <div style={{ padding: '0.75rem 1rem' }}>
        <span style={{ padding: '2px 8px', borderRadius: 6, background: form.accentColor + '22', color: form.accentColor, border: `1px solid ${form.accentColor}44`, fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase' }}>{t('branding.invoice')}</span>
        <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {[100, 80, 60].map(w => <div key={w} style={{ height: 7, background: '#f1f5f9', borderRadius: 3, width: `${w}%` }} />)}
        </div>
        <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', fontWeight: 700, fontSize: '0.75rem', color: form.primaryColor }}>
          <span>{t('branding.total')}</span><span>{t('branding.x15000Dzd')}</span>
        </div>
      </div>
      <div style={{ padding: '0.5rem 1rem', background: '#f8fafc', borderTop: '1px solid #f1f5f9', fontSize: '0.65rem', color: '#94a3b8', textAlign: 'center' }}>
        {form.storeName || 'Store Name'}
      </div>
    </PreviewShell>
  );

  // ─── Render ─────────────────────────────────────────────────────────────────
  return (
    <Shell>
      {/* Store list */}
      <StoreListPanel>
        <PanelHead><PanelTitle>{t('branding.retailStores')}</PanelTitle></PanelHead>
        <StoreScroll>
          {loading ? (
            <div style={{ padding: '1rem', color: 'var(--c-muted)', fontSize: '0.875rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
              <RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t('branding.loading')}
            </div>
          ) : stores.length === 0 ? (
            <div style={{ padding: '1rem', color: 'var(--c-muted)', fontSize: '0.875rem' }}>{t('branding.noRetailStores')}</div>
          ) : stores.map(s => (
            <StoreItem key={s._id} active={selected?._id === s._id} hasBranding={hasBranding(s)} onClick={() => selectStore(s)}>
              <Store size={13} style={{ flexShrink: 0 }} />
              <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
              <span className="dot" title={hasBranding(s) ? 'Custom branding' : 'Default branding'} />
            </StoreItem>
          ))}
        </StoreScroll>
      </StoreListPanel>

      {/* Editor */}
      <EditorPanel>
        {!selected ? (
          <EmptyHint>
            <Store size={56} />
            <strong>{t('branding.selectAStoreToConfigure')}</strong>
            <p>{t('branding.logoColorsInvoiceTemplateAnd')}</p>
          </EmptyHint>
        ) : (
          <>
            <TabBar>
              <Tab active={activeTab === 'branding'} onClick={() => setActiveTab('branding')}>
                <Palette size={13} /> {t('branding.branding')}
              </Tab>
              <Tab active={activeTab === 'invoice'} onClick={() => setActiveTab('invoice')}>
                <FileText size={13} /> {t('branding.invoiceLayout')}
              </Tab>
              <Tab active={activeTab === 'receipt'} onClick={() => setActiveTab('receipt')}>
                <Receipt size={13} /> {t('branding.receiptLayout')}
              </Tab>
              <Tab active={activeTab === 'numbering'} onClick={() => setActiveTab('numbering')}>
                <Hash size={13} /> {t('branding.orderNumbering')}
              </Tab>
            </TabBar>

            {/* ── Branding tab ── */}
            {activeTab === 'branding' && (
              <TabContent>
                {/* Logo */}
                <SectionCard>
                  <CardHead><Upload size={14} /> {t('branding.storeLogo')}</CardHead>
                  <CardBody>
                    <LogoZone drag={drag} onClick={() => fileRef.current?.click()}
                      onDragOver={e => { e.preventDefault(); setDrag(true); }}
                      onDragLeave={() => setDrag(false)} onDrop={handleDrop}>
                      {logoPreview ? (
                        <>
                          <LogoPreviewImg src={logoPreview} alt="preview" />
                          <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <span style={{ fontSize: '0.75rem', color: 'var(--c-muted)' }}>{t('branding.clickToReplace')}</span>
                            <button type="button" onClick={e => { e.stopPropagation(); setLogoFile(null); setLogoPreview(null); setRemoveLogo(true); }}
                              style={{ border: '1px solid var(--c-hairline)', background: 'none', borderRadius: 5, padding: '2px 8px', fontSize: '0.7rem', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 3, color: 'var(--c-muted)' }}>
                              <X size={10} /> {t('branding.remove')}
                            </button>
                          </div>
                        </>
                      ) : (
                        <>
                          <ImageIcon size={28} style={{ color: 'var(--c-muted)', opacity: 0.4 }} />
                          <span style={{ fontSize: '0.8rem', color: 'var(--c-muted)', textAlign: 'center' }}>
                            <strong>{t('branding.dropImage')}</strong> {t('branding.orClickToBrowse')}<br />{t('branding.pngJpgSvgMax5')}
                          </span>
                        </>
                      )}
                    </LogoZone>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleLogoInput} style={{ display: 'none' }} />
                  </CardBody>
                </SectionCard>

                {/* Identity */}
                <SectionCard>
                  <CardHead><Type size={14} /> {t('branding.storeIdentity')}</CardHead>
                  <CardBody>
                    <FieldGrid>
                      <FieldGroup>
                        <FieldLabel htmlFor="bs-name">{t('branding.storeName')}</FieldLabel>
                        <TextInput id="bs-name" value={form.storeName} onChange={e => setForm(f => ({ ...f, storeName: e.target.value }))} placeholder={t('branding.eGOptivaAlgiers')} />
                      </FieldGroup>
                      <FieldGroup>
                        <FieldLabel htmlFor="bs-tag">{t('branding.tagline')}</FieldLabel>
                        <TextInput id="bs-tag" value={form.tagline} onChange={e => setForm(f => ({ ...f, tagline: e.target.value }))} placeholder={t('branding.eGOpticalExcellence')} />
                      </FieldGroup>
                    </FieldGrid>
                  </CardBody>
                </SectionCard>

                {/* Colors */}
                <SectionCard>
                  <CardHead><Palette size={14} /> {t('branding.brandColors')}</CardHead>
                  <CardBody>
                    <ColorPickerRow>
                      {[
                        { key: 'primaryColor', label: 'Primary', desc: 'Headers, buttons, accents' },
                        { key: 'accentColor',  label: 'Accent',  desc: 'Badges, highlights' },
                      ].map(({ key, label, desc }) => (
                        <ColorBlock key={key}>
                          <FieldLabel>{label}</FieldLabel>
                          <ColorSwatch htmlFor={`clr-${key}`}>
                            <ColorCircle color={form[key]} />
                            <ColorHex>{form[key]}</ColorHex>
                            <HiddenColorInput id={`clr-${key}`} type="color" value={form[key]} onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))} />
                          </ColorSwatch>
                          <span style={{ fontSize: '0.7rem', color: 'var(--c-muted)', marginTop: 2 }}>{desc}</span>
                        </ColorBlock>
                      ))}
                    </ColorPickerRow>
                  </CardBody>
                </SectionCard>

                {/* Preview */}
                <SectionCard>
                  <CardHead><Eye size={14} /> {t('branding.livePreview2')}</CardHead>
                  <CardBody>
                    <PreviewCard />
                  </CardBody>
                </SectionCard>

                <ActionRow>
                  <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t('branding.saving')}</> : <><Save size={13} /> {t('branding.saveBranding')}</>}
                  </SaveBtn>
                  <GhostBtn onClick={handleReset} disabled={saving}><RefreshCw size={13} /> {t('branding.resetToDefaults')}</GhostBtn>
                  {feedback && (
                    <FeedbackBanner ok={feedback.ok}>
                      {feedback.ok ? <Check size={13} /> : <AlertCircle size={13} />} {feedback.msg}
                    </FeedbackBanner>
                  )}
                </ActionRow>
              </TabContent>
            )}

            {/* ── Invoice Layout tab ── */}
            {activeTab === 'invoice' && (
              <TabContent>
                {/* The invoice is a fixed Algerian facture now; this tab only
                    holds the store's legal details and shows the real invoice. */}
                <InvoiceLegalPanel
                  config={invoiceTpl}
                  onChange={setInvoiceTpl}
                  branding={{ ...form, logo: logoPreview || form.logo, phone: selected?.contact?.phone || selected?.phone || '', city: selected?.address?.city || '' }}
                  address={selected?.address}
                />
                <ActionRow>
                  <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t('branding.saving')}</> : <><Save size={13} /> {t('branding.saveInvoiceTemplate')}</>}
                  </SaveBtn>
                  {feedback && (
                    <FeedbackBanner ok={feedback.ok}>
                      {feedback.ok ? <Check size={13} /> : <AlertCircle size={13} />} {feedback.msg}
                    </FeedbackBanner>
                  )}
                </ActionRow>
              </TabContent>
            )}

            {/* ── Receipt Layout tab ── */}
            {activeTab === 'receipt' && (
              <TabContent>
                <BuilderShell>
                  <BuilderLeft>
                    <ReceiptBuilderPanel config={receiptTpl} onChange={setReceiptTpl} />
                  </BuilderLeft>
                  <BuilderRight>
                    <PreviewLabel>{t('branding.a5LandscapeReceiptPreview')}</PreviewLabel>
                    <ReceiptPreview
                      config={receiptTpl}
                      branding={{ ...form, phone: selected?.contact?.phone || '' }}
                      logoPreview={logoPreview}
                    />
                  </BuilderRight>
                </BuilderShell>
                <ActionRow>
                  <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t('branding.saving')}</> : <><Save size={13} /> {t('branding.saveReceiptTemplate')}</>}
                  </SaveBtn>
                  {feedback && (
                    <FeedbackBanner ok={feedback.ok}>
                      {feedback.ok ? <Check size={13} /> : <AlertCircle size={13} />} {feedback.msg}
                    </FeedbackBanner>
                  )}
                </ActionRow>
              </TabContent>
            )}

            {/* ── Order Numbering tab ── */}
            {activeTab === 'numbering' && (
              <TabContent>
                <OrderNumberingPanel
                  format={orderNumberFormat}
                  onFormatChange={setOrderNumberFormat}
                  cityCode={form.cityCode}
                  onCityCodeChange={(v) => setForm(f => ({ ...f, cityCode: v }))}
                  storeCode={form.storeCode}
                  onStoreCodeChange={(v) => setForm(f => ({ ...f, storeCode: v }))}
                />
                <ActionRow>
                  <SaveBtn onClick={handleSave} disabled={saving}>
                    {saving ? <><RefreshCw size={13} style={{ animation: 'spin 1s linear infinite' }} /> {t('branding.saving')}</> : <><Save size={13} /> {t('branding.saveNumbering')}</>}
                  </SaveBtn>
                  {feedback && (
                    <FeedbackBanner ok={feedback.ok}>
                      {feedback.ok ? <Check size={13} /> : <AlertCircle size={13} />} {feedback.msg}
                    </FeedbackBanner>
                  )}
                </ActionRow>
              </TabContent>
            )}
          </>
        )}
      </EditorPanel>
    </Shell>
  );
};

export default StoreBrandingSettings;
